const SIGNED_LINK_TYPE_OPTIONS = [
  { value: 'url', label: 'Add Record URL (generic link to use anywhere)' },
  { value: 'field', label: 'Update/Output/Add link (Button in Quickbase)' },
  { value: 'generate', label: 'Document Generation' },
  { value: 'import', label: 'Automatic Record Import' },
];

class SignedTemplateLinkCtrl {
  constructor(
    SchemaService,
    FEService,
    TBService,
    AuthService,
    TemplateService,
    SignedLinkSummaryBuilder,
    UrlClipCopy,
    $scope,
    $window,
    $timeout
  ) {
    'ngInject';
    Object.assign(this, { $scope, $window, $timeout });
    this.ss = SchemaService;
    this.fe = FEService;
    this.tb = TBService;
    this.as = AuthService;
    this.ts = TemplateService;
    this.summaryBuilder = SignedLinkSummaryBuilder;
    this.urlClipCopy = UrlClipCopy;

    this.newField = { fieldMap: {} };
    this.targets = [];
    this.targetRows = [];
    this.prefills = [];
    this.messages = [];
    this.tables = [];
    this.sourceSchema = {};
    this.sourceKeyFields = [];
    this.createdFieldUrl = '';
    this.createdFieldName = '';
    this.createdShareSummary = null;
    this.initializing = true;
    this.saving = false;
    this.loadingHostSchema = false;
    this.showForm = false;
    this.prefillDraftIncomplete = false;
    this._eligibleReferenceIds = [];
    this._passkeyEligibleReferenceIds = [];
    this.forcePdfDownload = false;
    this.enableSaveAndKeepWorking = true;
    this.linkType = 'url';
    this._lastLinkType = 'url';
    this.linkTypeOptions = [...SIGNED_LINK_TYPE_OPTIONS];
    this.linkRedirect = { referenceId: null, field: null };
    this.redirectOnLoad = false;
    this.linkSuccessMessage = { referenceId: null, field: null };
    this.linkUpload = {
      referenceId: null,
      uploadField: null,
      filenameField: null,
      uploadType: '',
      generateOnPrint: false,
    };
    this.parseDocField = null;
    this.uploadExtensions = [
      { text: 'PDF (.pdf)', value: '' },
      { text: 'Word (.docx)', value: 'docx' },
      { text: 'PowerPoint (.pptx)', value: 'pptx' },
      { text: 'Image (.jpg)', value: 'jpg' },
    ];
    this.linkPasskeys = [];
    this.linkPasskeyMessage = '';
    this.showLinkPasskeyMessageHtml = false;
    this.draftLinkPasskey = { referenceId: null, passkeyField: null, placeholder: '' };
    this.draftPasskeyFieldOptions = [];
    this._targetTableSchemas = {};
    this.tableOptionsTargets = [];
    this.uploadFieldOptions = [];
    this.filenameFieldOptions = [];
    this.redirectFieldOptions = [];
    this.successMessageFieldOptions = [];
    this.passkeyReferenceOptions = [];
    this.importDocFieldOptions = [];
    this._passkeyReferenceOptionsSignature = '';
    this._importDocFieldOptionsSignature = '';
    this.wizardStepCount = 5;
    this.summaryStepNumber = 6;
    this.wizardNavSteps = [];
    this.wizardDisplayStepNumber = 1;
    this.linkSummary = null;
    this.draftHighlightSection = null;
    this.wizardStep = 1;
    this.isEditMode = false;
    this.editLinkId = null;
    this._lastHostTableId = '';
    this._tablesReady = false;
    this._editHydrated = false;
    this.requireSuppliedConnForAutoGen = false;
    this.suppliedConnection = null;
  }

  isIgnoredTargetAction(action) {
    return action === 'ignore';
  }

  defaultActionForNewTargetRow() {
    if (this.isEditModeActive()) return 'ignore';
    if (this.isAutomaticFileGenerationLink()) return 'print';
    return 'submit';
  }

  isDefaultTargetRow(row) {
    if (!row || this.isIgnoredTargetAction(row.action)) return false;
    if (this.isAutomaticFileGenerationLink()) {
      if (row.action !== 'print') return false;
      return !this.hasParentFieldSelected(row.parentField);
    }
    if (row.action === 'update' || row.action === 'print') return false;
    if (this.hasParentFieldSelected(row.parentField)) return false;
    if (row.onEmptyRecordAction === 'ignore') return false;
    if (row.recordNotFoundAction && row.recordNotFoundAction !== 'error') return false;
    return row.action === 'submit';
  }

  hasConfiguredTargetMapping() {
    const rows = this.targetRows || [];
    if (rows.length) {
      return rows.some((row) => !this.isDefaultTargetRow(row));
    }
    return (this.targets || []).some((target) => {
      if (target.action !== 'submit') return true;
      if (this.hasParentFieldSelected(target.parentField)) return true;
      if (target.onEmptyRecordAction === 'ignore') return true;
      if (target.recordNotFoundAction && target.recordNotFoundAction !== 'error') return true;
      return false;
    });
  }

  wouldLoseHostTableConfiguration() {
    return (this.prefills || []).length > 0 || this.hasConfiguredTargetMapping();
  }

  ensureTargetRows() {
    if (!(this.availableReferences || []).length) {
      this.targetRows = [];
      this.syncTargetsFromRows();
      return;
    }
    if (!(this.targetRows || []).length) {
      this.initTargetRows();
    }
  }

  enterMappingStep() {
    this.ensureTargetRows();
    this.syncTargetsFromRows();
  }

  initTargetRows() {
    const existingByRef = new Map((this.targets || []).map((t) => [t.referenceId, t]));
    const defaultForNew = this.defaultActionForNewTargetRow();
    this.targetRows = (this.availableReferences || [])
      .map((ref) => {
        const existing = existingByRef.get(ref.id);
        if (existing) {
          return {
            referenceId: ref.id,
            referenceName: ref.name || ref.id,
            action: existing.action,
            parentField: existing.parentField || null,
            recordNotFoundAction: existing.recordNotFoundAction || 'error',
            onEmptyRecordAction: existing.onEmptyRecordAction || 'create',
          };
        }
        return {
          referenceId: ref.id,
          referenceName: ref.name || ref.id,
          action: defaultForNew,
          parentField: null,
          recordNotFoundAction: 'error',
          onEmptyRecordAction: 'create',
        };
      })
      .sort((a, b) => String(a.referenceName).localeCompare(String(b.referenceName)));
    this.syncTargetsFromRows();
  }

  buildTargetsFromRows() {
    return (this.targetRows || [])
      .filter((row) => row.action && !this.isIgnoredTargetAction(row.action))
      .map((row) => ({
        referenceId: row.referenceId,
        referenceName: row.referenceName,
        action: row.action,
        parentField:
          row.action === 'update' || row.action === 'print' ? row.parentField || null : null,
        ...((row.action === 'update' || row.action === 'print') && this.isTableLink()
          ? { recordNotFoundAction: this.normalizeRecordNotFoundAction(row.recordNotFoundAction) }
          : {}),
        ...(this.shouldIncludeOnEmptyRecordAction(row)
          ? { onEmptyRecordAction: this.normalizeOnEmptyRecordAction(row.onEmptyRecordAction) }
          : {}),
      }));
  }

  syncTargetsFromRows() {
    this.targets = this.buildTargetsFromRows();
    this.updateEligibleReferenceIds();
  }

  targetRowNeedsParentField(row) {
    return row?.action === 'update' || row?.action === 'print';
  }

  targetRowShowsRecordNotFound(row) {
    return this.isTableLink() && this.targetRowNeedsParentField(row);
  }

  hasAutoGenPrintTarget() {
    return (this.targetRows || []).some(
      (row) => row.action === 'print' && this.targetHasRequiredParentField({
        action: row.action,
        parentField: row.parentField,
      })
    );
  }

  autoGenUploadValid() {
    if (!this.isAutomaticFileGenerationLink()) return true;
    const hostTableId = this.newField?.tableId;
    const refId = this.linkUpload?.referenceId;
    const uploadFieldId = this.linkUpload?.uploadField?.id;
    return !!(hostTableId && refId && uploadFieldId != null);
  }

  linkUploadContextReady() {
    return !!this.linkUpload?.referenceId;
  }

  linkUploadRefsForOptions() {
    const ref = this.linkUpload?.referenceId
      ? this.referenceForTableOptions(this.linkUpload.referenceId)
      : null;
    return ref ? [ref] : [];
  }

  linkUploadSchemaFields() {
    const refs = this.linkUploadRefsForOptions();
    return refs[0]?.tableSchema?.fields || [];
  }

  uploadAttachmentEmptyReason() {
    if (!this.linkUploadContextReady()) return '';
    if (this.uploadFieldOptions?.length) return '';
    const fields = this.linkUploadSchemaFields();
    if (!fields.length) return '';
    const fileFields = fields.filter((f) => f.isFile);
    if (!fileFields.length) {
      return 'No attachment fields on this table.';
    }
    const refs = this.linkUploadRefsForOptions();
    const eligibleIds = new Set();
    refs.forEach((ref) => {
      this.tb.getRefUploadFields(this.ts.template, ref, this.PROVIDER).forEach((f) => {
        eligibleIds.add(String(f.id));
      });
    });
    if (!eligibleIds.size) {
      return 'All attachment fields on this table are already used on the form.';
    }
    return 'No attachment fields available on this table.';
  }

  filenameFieldEmptyReason() {
    if (!this.linkUploadContextReady()) return '';
    if (this.filenameFieldOptions?.length) return '';
    if (!(this.PROVIDER?.filenameFields?.length)) return '';
    const fields = this.linkUploadSchemaFields();
    if (!fields.length) return '';
    const filenameCandidates = fields.filter((f) =>
      this.PROVIDER.filenameFields.includes(f.fieldType)
    );
    if (!filenameCandidates.length) {
      return 'No filename fields on this table.';
    }
    return '';
  }

  targetRowShowsOnEmptyRecord(row) {
    return this.shouldIncludeOnEmptyRecordAction(row);
  }

  // Submit always; update/print only when create-on-not-found is selected.
  shouldIncludeOnEmptyRecordAction(row) {
    if (!row?.action) return false;
    if (row.action === 'submit') return true;
    if ((row.action === 'update' || row.action === 'print') && this.isTableLink()) {
      return this.normalizeRecordNotFoundAction(row.recordNotFoundAction) === 'create';
    }
    return false;
  }

  targetRowActionHint(row) {
    const action = row?.action;
    if (action === 'update') return 'Open and edit an existing record.';
    if (action === 'print') return 'Open read-only for print or download.';
    if (action === 'ignore') return 'This table is not included in the link.';
    return 'A new record is saved on submit.';
  }

  targetRowRecordNotFoundHint(row) {
    const value = this.normalizeRecordNotFoundAction(row?.recordNotFoundAction);
    if (value === 'warn') return "Continue with a warning if the record isn't found.";
    if (value === 'ignore') return "Continue silently if the record isn't found.";
    if (value === 'create') return "Create a new record if the record key doesn't match any record.";
    return "Block with an error if the record key doesn't match any record.";
  }

  targetRowOnEmptyRecordHint(row) {
    const value = this.normalizeOnEmptyRecordAction(row?.onEmptyRecordAction);
    if (value === 'ignore') return 'Skip creating a record if no data was entered.';
    return 'Create a new record even if the user submits without entering data.';
  }

  onTargetRowActionChange(row) {
    if (!row) return;
    if (this.isUrlLink() && row.action !== 'ignore' && row.action !== 'submit') {
      row.action = 'submit';
    }
    if (this.isAutomaticFileGenerationLink() && row.action !== 'ignore' && row.action !== 'print') {
      row.action = 'print';
    }
    if (!this.targetRowNeedsParentField(row)) {
      row.parentField = null;
      row.recordNotFoundAction = 'error';
    }
    if (this.shouldIncludeOnEmptyRecordAction(row)) {
      row.onEmptyRecordAction = this.normalizeOnEmptyRecordAction(row.onEmptyRecordAction);
    } else {
      row.onEmptyRecordAction = 'create';
    }
    this.onTargetRowDetailChange(row);
  }

  onRecordNotFoundActionChange(row) {
    if (!row) return;
    if (this.normalizeRecordNotFoundAction(row.recordNotFoundAction) === 'create') {
      row.onEmptyRecordAction = this.normalizeOnEmptyRecordAction(row.onEmptyRecordAction);
    } else {
      row.onEmptyRecordAction = 'create';
    }
    this.onTargetRowDetailChange(row);
  }

  isDynamicTemplate() {
    return this.ts.template?.type === 'dynamic';
  }

  syncLinkTypeOptions() {
    this.linkTypeOptions = SIGNED_LINK_TYPE_OPTIONS.filter(
      (opt) => opt.value !== 'import' || !this.isDynamicTemplate()
    );
  }

  onTargetRowDetailChange(_row) {
    this.syncTargetsFromRows();
    this.syncWizardSelectOptions();
    this.prunePrefillsForTargets();
    this.pruneLinkUploadForTargets();
    this.pruneDownloadOnOpenForTargets();
    this.pruneLinkPasskeysForTargets();
    this.pruneLinkActionFieldsForTargets();
  }

  async $onInit() {
    if (this.editLink?.linkId) {
      this.isEditMode = true;
      this.editLinkId = this.editLink.linkId;
    }
    try {
      const [allDbs, PROVIDER] = await Promise.all([
        this.ss.getGrantedDBs(),
        this.as.getProviderSettings(),
        this.ts.getTemplateData(),
      ]);
      this.PROVIDER = PROVIDER;
      this.requireSuppliedConnForAutoGen = !(
        (this.ts.template.connectionProperties || []).includes('usertoken')
      );
      this.tables = allDbs.filter(
        (db) => db.appId === this.ts.template.appId && !!db.tableName
      );
      this.syncLinkTypeOptions();
      if (!this.editLink) {
        this.newField.label = this.defaultLinkLabelForType('url');
        this.linkType = 'url';
        this.newField.tableId = '';
        this.applyUrlLinkMode();
        this._lastLinkType = 'url';
      }
      this.availableReferences = (this.ts.references || []).filter((r) => !r._temp);
      if (!this.editLink) {
        this.initTargetRows();
      }
      this._tablesReady = true;
      await this.maybeHydrateEditLink();
    } catch (error) {
      this.handleMessage({ error, when: 'During Page Setup' });
    }
    this.initializing = false;
    this.updateEligibleReferenceIds();
    this.syncWizardSelectOptions();
  }

  $onChanges(changes) {
    if (!changes.editLink?.currentValue || this._editHydrated || !this._tablesReady) return;
    this.maybeHydrateEditLink();
  }

  async maybeHydrateEditLink() {
    if (!this.editLink || this._editHydrated) return;
    this._editHydrated = true;
    const link = await this.resolveEditLinkForHydrate();
    if (!link) {
      this.handleMessage({ error: new Error('The link could not be loaded.'), when: 'Loading link' });
      return;
    }
    this.editLink = link;
    await this.applyLinkForEdit(link);
    this.syncWizardNav();
    this.syncWizardSelectOptions();
    await this.applyInitialUploadStepFocus();
    this.updateEligibleReferenceIds();
    this.syncWizardSelectOptions();
    if (this.initialWizardStepId != null && this.initialWizardStepId !== '') {
      this.setWizardStep(Number(this.initialWizardStepId));
      this.syncWizardNav();
      this.syncWizardSelectOptions();
      this.syncPasskeyMessageEditorForStep();
    }
  }

  async resolveEditLinkForHydrate() {
    const link = this.editLink;
    if (!link?.linkId) return null;
    if (link.targets != null || link.options != null || link.field != null) {
      return link;
    }
    return this.fetchLinkById(link.linkId);
  }

  async fetchLinkById(linkId) {
    const templateId = this.ts.template?.id;
    if (!templateId) return null;
    const request = this.as.includeConnectionInRequest({
      payload: { action: 'listLinks', templateId },
    });
    const response = await this.ts.linkManagement(request);
    const links = (response?.data && response.data.links) || [];
    return links.find((l) => String(l.linkId) === String(linkId)) || null;
  }

  async applyInitialUploadStepFocus() {
    const refId = this.preselectUploadReferenceId;
    if (!refId || this.linkUpload?.referenceId) return;
    if (!this.referenceForTableOptions(refId)) return;
    this.linkUpload.referenceId = refId;
    await this.onLinkUploadReferenceChange();
  }

  isDefaultLinkEditWithUploadStep() {
    return this.isEditModeActive() && this.isDefaultTemplateLink() && this.isUrlLink();
  }

  isEditModeActive() {
    return this.isEditMode === true;
  }

  isUrlLink() {
    if (this.isEditModeActive()) {
      return this.editLink?.type === 'url';
    }
    return this.linkType === 'url';
  }

  isTableLink() {
    if (this.isEditModeActive()) {
      return this.editLink?.type === 'field';
    }
    return this.linkType === 'field';
  }

  isAutomaticFileGenerationLink() {
    if (this.isEditModeActive()) {
      return this.editLink?.type === 'generate';
    }
    return this.linkType === 'generate';
  }

  isAutomaticRecordImportLink() {
    if (this.isEditModeActive()) {
      return this.editLink?.type === 'import';
    }
    return this.linkType === 'import';
  }

  isWebhookPublicationLink() {
    return this.isAutomaticFileGenerationLink() || this.isAutomaticRecordImportLink();
  }

  requiresAutoGenSuppliedConnection() {
    if (this.isEditModeActive()) return false;
    if (!this.isWebhookPublicationLink()) return false;
    return this.requireSuppliedConnForAutoGen === true;
  }

  autoGenSuppliedConnectionValid() {
    if (!this.requiresAutoGenSuppliedConnection()) return true;
    const token = this.suppliedConnection?.userToken;
    return typeof token === 'string' && token.trim().length > 0;
  }

  initAutoGenSuppliedConnection() {
    if (!this.requiresAutoGenSuppliedConnection()) {
      this.suppliedConnection = null;
      return;
    }
    if (!this.suppliedConnection) this.suppliedConnection = {};
  }

  isTableHostedLink() {
    return this.isTableLink() || this.isWebhookPublicationLink();
  }

  needsHostTable() {
    return this.isTableHostedLink();
  }

  resolveLinkTypeFromLink(link) {
    if (link?.type === 'url' || link?.type === 'field' || link?.type === 'generate' || link?.type === 'import') {
      return link.type;
    }
    return 'field';
  }

  linkTypeDisplayLabel() {
    if (this.isUrlLink()) return 'Add Record URL (generic link to use anywhere)';
    if (this.isAutomaticFileGenerationLink()) return 'Document Generation';
    if (this.isAutomaticRecordImportLink()) return 'Automatic Record Import';
    return 'Update/Output/Add link (Button in Quickbase)';
  }

  defaultLinkLabelForType(linkType = this.linkType) {
    const title = this.ts?.template?.title || 'Form';
    if (linkType === 'generate') return `Generate ${title}`;
    if (linkType === 'import') return `Parse ${title}`;
    return `Open ${title}`;
  }

  syncDefaultLinkLabelForType(linkType, prevType) {
    const current = String(this.newField?.label || '').trim();
    const prevDefault = this.defaultLinkLabelForType(prevType);
    if (!current || current === prevDefault) {
      this.newField.label = this.defaultLinkLabelForType(linkType);
    }
  }

  isDownloadOnlyLink() {
    return this.buildTargetsFromRows().length === 0;
  }

  isPrintOnlyLink() {
    const targets = this.buildTargetsFromRows();
    return targets.length > 0 && targets.every((t) => t.action === 'print');
  }

  canOfferDownloadOnOpen() {
    if (this.isAutomaticFileGenerationLink() || this.isAutomaticRecordImportLink()) return false;
    const rows = this.targetRows || [];
    if (!rows.length) return false;
    return rows.every((row) => {
      const action = row.action || 'ignore';
      return action === 'print' || action === 'ignore';
    });
  }

  isDefaultTemplateLink() {
    return this.editLinkId != null
      && this.ts.template?.defaultTemplateLinkId != null
      && String(this.editLinkId) === String(this.ts.template.defaultTemplateLinkId);
  }

  modalTitle() {
    if (!this.editLink) return 'Create Template Link';
    if (this.isDefaultTemplateLink()) return 'Edit Default URL Link';
    if (this.editLink?.type === 'url') return 'Edit URL Link';
    if (this.isAutomaticFileGenerationLink()) return 'Edit Document Generation Link';
    if (this.isAutomaticRecordImportLink()) return 'Edit Automatic Record Import Link';
    return 'Edit Smart Link';
  }

  resolveEnableSaveAndKeepWorkingForPayload(downloadOnly) {
    if (this.isDefaultTemplateLink()) return true;
    if (downloadOnly || this.isWebhookPublicationLink()) return false;
    return this.enableSaveAndKeepWorking;
  }

  hasOptionalLinkSteps() {
    return !this.isDownloadOnlyLink();
  }

  isUploadWizardStep() {
    if (this.isAutomaticFileGenerationLink()) return this.isWizardStep(3);
    if (this.isDefaultLinkEditWithUploadStep()) return this.isWizardStep(3);
    return this.isWizardStep(3) && this.hasOptionalLinkSteps();
  }

  isActionsWizardStep() {
    if (this.isAutomaticFileGenerationLink()) return false;
    return this.isWizardStep(5) && this.hasOptionalLinkSteps();
  }

  clearLinkActionFields() {
    this.linkRedirect = { referenceId: null, field: null };
    this.redirectOnLoad = false;
    this.linkSuccessMessage = { referenceId: null, field: null };
  }

  applyAutomaticFileGenerationMode() {
    this.ensureTargetRows();
    (this.targetRows || []).forEach((row) => {
      if (this.isIgnoredTargetAction(row.action)) return;
      if (row.action !== 'print') {
        row.action = 'print';
      }
      row.onEmptyRecordAction = 'create';
    });
    this.syncTargetsFromRows();
    this.prefills = [];
    this.linkPasskeys = [];
    this.linkPasskeyMessage = '';
    this.clearLinkActionFields();
    this.resetLinkPasskeys();
    this._targetTableSchemas = {};
  }

  applyAutomaticRecordImportMode() {
    this.targets = [];
    this.targetRows = [];
    this.prefills = [];
    this.linkPasskeys = [];
    this.linkPasskeyMessage = '';
    this.clearLinkActionFields();
    this.resetLinkUpload();
    this.resetLinkPasskeys();
    this.parseDocField = null;
    this._targetTableSchemas = {};
  }

  applyUrlLinkMode() {
    this.linkType = 'url';
    this.newField.tableId = '';
    this.sourceSchema = { fields: [] };
    this.sourceKeyFields = [];
    (this.targetRows || []).forEach((row) => {
      if (row.action === 'update' || row.action === 'print') {
        row.action = 'submit';
        row.parentField = null;
        row.recordNotFoundAction = 'error';
      }
    });
    this.syncTargetsFromRows();
    this.prefills = [];
    this.linkPasskeys = [];
    this.linkPasskeyMessage = '';
    this.linkRedirect = { referenceId: null, field: null };
    this.redirectOnLoad = false;
    this.linkSuccessMessage = { referenceId: null, field: null };
    this.resetLinkUpload();
    this.resetLinkPasskeys();
    this._targetTableSchemas = {};
  }

  canMapTargets() {
    return this.isUrlLink() || !!this.newField?.tableId;
  }

  resolveHostTableId(tableId) {
    if (tableId == null || tableId === '') return '';
    const match = (this.tables || []).find((t) => String(t.tableId) === String(tableId));
    return match ? match.tableId : tableId;
  }

  hostTableDisplayLabel() {
    if (this.isUrlLink()) return '';
    const linkName = this.editLink?.table?.name;
    if (linkName) return linkName;
    const tableId = this.newField?.tableId;
    if (tableId == null || tableId === '') return '';
    const match = (this.tables || []).find((t) => String(t.tableId) === String(tableId));
    return match?.tableName || String(tableId);
  }

  async loadHostTableSchema(tableId) {
    if (!tableId) return;
    this.sourceSchema = await this.ss.getSchema(this.ts.template, tableId);
    this.sourceKeyFields = this.getKeyFields(this.sourceSchema.fields);
  }

  getKeyFields(fields) {
    const keyFieldTypes = ['float', 'text'];
    return (fields || []).filter(
      (f) => f.isForeignKey || f.isPrimaryKey || keyFieldTypes.includes(f.fieldType)
    );
  }

  findFieldOnReference(referenceId, fieldId) {
    if (fieldId == null || fieldId === '') return null;
    const ref = this.referenceForTableOptions(referenceId);
    const fields = ref?.tableSchema?.fields || [];
    return fields.find((f) => String(f.id) === String(fieldId)) || {
      id: String(fieldId),
      label: String(fieldId),
    };
  }

  async applyLinkForEdit(link) {
    this.isEditMode = true;
    this.editLinkId = link.linkId;
    this.newField.label = link.field?.label || '';
    this.linkType = this.resolveLinkTypeFromLink(link);
    this._lastLinkType = this.linkType;
    this.newField.tableId = this.isUrlLink()
      ? ''
      : this.resolveHostTableId(link.table?.id);
    this.forcePdfDownload = !!link.options?.downloadOnOpen;
    this.enableSaveAndKeepWorking = this.isDefaultTemplateLink()
      ? true
      : this.isWebhookPublicationLink()
        ? false
        : link.options?.enableSaveAndKeepWorking !== false;

    if (this.isUrlLink()) {
      this.sourceSchema = { fields: [] };
      this.sourceKeyFields = [];
    } else {
      await this.loadHostTableSchema(this.newField.tableId);
      if (this.isAutomaticRecordImportLink()) {
        await this.applyParseFieldFromOptions(link.options?.parseField);
      }
    }

    this.targets = await Promise.all((link.targets || []).map(async (t) => {
      const ref = this.availableReferences.find((r) => r.id === t.referenceId);
      let parentField = null;
      if (t.parentFieldId != null) {
        parentField = (this.sourceKeyFields || []).find(
          (f) => String(f.id) === String(t.parentFieldId)
        ) || { id: String(t.parentFieldId), label: String(t.parentFieldId) };
      }
      return {
        referenceId: t.referenceId,
        referenceName: ref?.name || t.referenceId,
        action: t.action,
        parentField,
        ...(t.action === 'update' || t.action === 'print'
          ? { recordNotFoundAction: this.normalizeRecordNotFoundAction(t.recordNotFoundAction) }
          : {}),
        ...(this.shouldIncludeOnEmptyRecordAction({
          action: t.action,
          recordNotFoundAction: t.recordNotFoundAction,
        })
          ? { onEmptyRecordAction: this.normalizeOnEmptyRecordAction(t.onEmptyRecordAction) }
          : {}),
      };
    }));

    await this.applyLinkUploadFromOptions(link.options?.upload);
    await this.applyLinkPasskeysFromOptions(link.options?.passkey);
    await this.applyLinkRedirectFromOptions(link.options?.redirect);
    await this.applyLinkSuccessMessageFromOptions(link.options?.successMessage);
    await this.applyPrefillsFromLink(link.prefills);
    this.initTargetRows();
  }

  async applyParseFieldFromOptions(parseField) {
    if (!parseField?.fieldId) {
      this.parseDocField = null;
      return;
    }
    const match = (this.sourceSchema?.fields || []).find(
      (f) => String(f.id) === String(parseField.fieldId)
    );
    this.parseDocField = match || {
      id: String(parseField.fieldId),
      label: String(parseField.fieldId),
      fieldType: 'file',
    };
  }

  filterFileFields() {
    return (field) => field.fieldType === 'file';
  }

  selectOptionsSignature(options, idKey = 'id', labelKey = 'name') {
    return (options || [])
      .map((o) => `${o?.[idKey]}::${o?.[labelKey]}`)
      .join('|');
  }

  syncImportDocFieldOptions() {
    const next = (this.sourceSchema?.fields || []).filter((f) => f.fieldType === 'file');
    const signature = this.selectOptionsSignature(next, 'id', 'label');
    if (signature === this._importDocFieldOptionsSignature) return;
    this._importDocFieldOptionsSignature = signature;
    this.importDocFieldOptions = next;
  }

  syncPasskeyReferenceOptions() {
    const eligible = new Set(this._passkeyEligibleReferenceIds || []);
    const used = new Set((this.linkPasskeys || []).map((p) => p.referenceId));
    const next = (this.tableOptionsTargets || []).filter(
      (t) => eligible.has(t.id) && !used.has(t.id)
    );
    const signature = this.selectOptionsSignature(next, 'id', 'name');
    if (signature === this._passkeyReferenceOptionsSignature) return;
    this._passkeyReferenceOptionsSignature = signature;
    this.passkeyReferenceOptions = next;
  }

  importParseFieldValid() {
    return this.parseDocField?.id != null;
  }

  importBasicInfoValid() {
    return this.stepHostValid() && this.importParseFieldValid();
  }

  getImportParseFieldBlockedReason() {
    if (!this.newField?.tableId) return 'Select a launch table on the Basic Info step.';
    if (!this.sourceSchema?.fields?.length) return 'Load the launch table schema first.';
    if (!this.importParseFieldValid()) return 'Select the field containing the PDF to be parsed.';
    return '';
  }

  async applyLinkUploadFromOptions(upload) {
    if (!upload?.referenceId) {
      this.resetLinkUpload();
      return;
    }
    this.linkUpload.referenceId = upload.referenceId;
    await this.loadTargetTableSchema(upload.referenceId);
    const ref = this.referenceForTableOptions(upload.referenceId);
    const uploadFields = ref ? this.tb.getRefUploadFields(this.ts.template, ref, this.PROVIDER) : [];
    const filenameFields = ref ? this.tb.getRefFilenameFields(ref, this.PROVIDER) : [];
    if (upload.uploadFieldId) {
      this.linkUpload.uploadField = uploadFields.find(
        (f) => String(f.id) === String(upload.uploadFieldId)
      ) || null;
    }
    if (upload.filenameFieldId) {
      this.linkUpload.filenameField = filenameFields.find(
        (f) => String(f.id) === String(upload.filenameFieldId)
      ) || null;
    }
    this.linkUpload.uploadType = upload.uploadType ?? '';
    this.linkUpload.generateOnPrint = upload.genActions?.print === true;
  }

  async applyLinkPasskeysFromOptions(passkey) {
    this.linkPasskeys = [];
    this.linkPasskeyMessage = passkey?.passkeyMessage || '';
    if (!passkey?.entries?.length) return;
    for (const entry of passkey.entries) {
      const ref = (this.targets || []).find((t) => t.referenceId === entry.referenceId);
      await this.loadTargetTableSchema(entry.referenceId);
      const refForFields = this.referenceForTableOptions(entry.referenceId);
      const passkeyFields = refForFields ? this.tb.getPasskeyFields(refForFields, this.PROVIDER) : [];
      const passkeyField = passkeyFields.find(
        (f) => String(f.id) === String(entry.passkeyFieldId)
      );
      if (!passkeyField) continue;
      this.linkPasskeys.push({
        referenceId: entry.referenceId,
        referenceName: ref?.referenceName || entry.referenceId,
        passkeyField,
        placeholder: entry.passkeyPlaceholder || '',
      });
    }
  }

  async applyLinkRedirectFromOptions(redirect) {
    if (!redirect?.referenceId || !redirect.fieldId) {
      this.linkRedirect = { referenceId: null, field: null };
      this.redirectOnLoad = false;
      return;
    }
    this.linkRedirect.referenceId = redirect.referenceId;
    await this.loadTargetTableSchema(redirect.referenceId);
    this.linkRedirect.field = this.findFieldOnReference(redirect.referenceId, redirect.fieldId);
    this.redirectOnLoad = this.isUrlLink() ? false : !!redirect.onLoad;
  }

  async applyLinkSuccessMessageFromOptions(successMessage) {
    if (!successMessage?.referenceId || !successMessage.fieldId) {
      this.linkSuccessMessage = { referenceId: null, field: null };
      return;
    }
    this.linkSuccessMessage.referenceId = successMessage.referenceId;
    await this.loadTargetTableSchema(successMessage.referenceId);
    this.linkSuccessMessage.field = this.findFieldOnReference(
      successMessage.referenceId,
      successMessage.fieldId
    );
  }

  async applyPrefillsFromLink(prefills) {
    this.prefills = [];
    for (const p of prefills || []) {
      const ref = this.availableReferences.find((r) => r.id === p.referenceId);
      if (!ref) continue;
      await this.loadTargetTableSchema(p.referenceId);
      const targetField = this.findFieldOnReference(p.referenceId, p.targetFieldId);
      const sourceField = (this.sourceSchema?.fields || []).find(
        (f) => String(f.id) === String(p.sourceFieldId)
      );
      if (!targetField || !sourceField) continue;
      this.prefills.push({
        reference: ref,
        targetField,
        sourceField,
      });
    }
  }

  blockEnterSubmit($event) {
    if ($event?.key === 'Enter' || $event?.keyCode === 13) {
      $event.preventDefault();
      $event.stopPropagation();
    }
  }

  currentWizardStepId() {
    return Number(this.wizardStep);
  }

  setWizardStep(step) {
    this.wizardStep = Number(step);
  }

  syncWizardNav() {
    this.rebuildWizardNav();
    this.normalizeWizardStepAfterNavChange();
    this.refreshWizardDisplayStepNumber();
  }

  isWizardStep(step) {
    return this.currentWizardStepId() === Number(step);
  }

  areWizardNavStepsEqual(next, prev) {
    if (!prev || next.length !== prev.length) return false;
    return next.every(
      (step, index) =>
        step.id === prev[index].id &&
        step.label === prev[index].label &&
        step.number === prev[index].number
    );
  }

  refreshWizardDisplayStepNumber() {
    const current = this.currentWizardStepId();
    let step = (this.wizardNavSteps || []).find((s) => s.id === current);
    if (!step) {
      const nextSteps = this.buildWizardNavSteps();
      if (!this.areWizardNavStepsEqual(nextSteps, this.wizardNavSteps)) {
        this.wizardNavSteps = nextSteps;
        this.wizardStepCount = nextSteps.length;
      }
      step = this.wizardNavSteps.find((s) => s.id === current);
    }
    const nextDisplay = step?.number ?? 1;
    if (this.wizardDisplayStepNumber !== nextDisplay) {
      this.wizardDisplayStepNumber = nextDisplay;
    }
  }

  hasPrefillStep() {
    if (this.isWebhookPublicationLink()) return false;
    if (!this.hasOptionalLinkSteps() || this.isUrlLink()) return false;
    return this.hasPrefillEligibleTargets();
  }

  hasPasskeyStep() {
    if (this.isWebhookPublicationLink()) return false;
    if (!this.hasOptionalLinkSteps() || this.isUrlLink()) return false;
    return this.hasPasskeyEligibleTargets();
  }

  hasActionsStep() {
    if (this.isWebhookPublicationLink()) return false;
    if (!this.hasOptionalLinkSteps()) return false;
    return this.isTableLink() || this.isUrlLink();
  }

  isPrefillWizardStep() {
    return this.isWizardStep(6) && this.hasPrefillStep();
  }

  isSummaryStep() {
    return this.isWizardStep(this.summaryStepNumber);
  }

  isRequiredWizardNavStep(stepId) {
    const id = Number(stepId);
    if (id === this.summaryStepNumber) return false;
    if (this.isAutomaticRecordImportLink()) return id === 1;
    if (this.isAutomaticFileGenerationLink()) return id >= 1 && id <= 3;
    return id < 3;
  }

  getWizardStepLabel(stepId) {
    const labels = {
      1: 'Basic Info',
      2: 'Tables',
      3: 'File Upload',
      4: 'Passkey',
      5: 'Actions',
      6: 'Pre-fill',
    };
    if (stepId === this.summaryStepNumber) return 'Review';
    return labels[stepId] || '';
  }

  getApplicableWizardStepIds() {
    if (this.isAutomaticRecordImportLink()) {
      this.summaryStepNumber = 2;
      return [1, 2];
    }
    if (this.isAutomaticFileGenerationLink()) {
      this.summaryStepNumber = 4;
      return [1, 2, 3, 4];
    }
    if (this.isDownloadOnlyLink()) {
      if (this.isDefaultLinkEditWithUploadStep()) {
        this.summaryStepNumber = 4;
        return [1, 2, 3, 4];
      }
      this.summaryStepNumber = 3;
      return [1, 2, 3];
    }
    this.summaryStepNumber = this.isTableLink() && this.hasPrefillStep() ? 7 : 6;
    const ids = [1, 2, 3];
    if (this.isTableLink() && this.hasPasskeyStep()) ids.push(4);
    if (this.hasActionsStep()) ids.push(5);
    if (this.isTableLink() && this.hasPrefillStep()) ids.push(6);
    ids.push(this.summaryStepNumber);
    return ids;
  }

  buildWizardNavSteps() {
    let number = 0;
    return this.getApplicableWizardStepIds().map((id) => ({
      id,
      label: this.getWizardStepLabel(id),
      number: ++number,
    }));
  }

  rebuildWizardNav() {
    const nextSteps = this.buildWizardNavSteps();
    if (!this.areWizardNavStepsEqual(nextSteps, this.wizardNavSteps)) {
      this.wizardNavSteps = nextSteps;
      this.wizardStepCount = nextSteps.length;
    }
  }

  getNextWizardStepId(fromId) {
    const ids = this.getApplicableWizardStepIds();
    const id = Number(fromId);
    const idx = ids.indexOf(id);
    if (idx === -1 || idx >= ids.length - 1) return id;
    return ids[idx + 1];
  }

  getPrevWizardStepId(fromId) {
    const ids = this.getApplicableWizardStepIds();
    const id = Number(fromId);
    const idx = ids.indexOf(id);
    if (idx <= 0) return id;
    return ids[idx - 1];
  }

  normalizeWizardStepAfterNavChange() {
    const current = this.currentWizardStepId();
    const ids = this.getApplicableWizardStepIds();
    if (ids.includes(current)) {
      if (this.wizardStep !== current) this.wizardStep = current;
      return;
    }
    if (current === 4 && !this.hasPasskeyStep()) {
      this.destroyLinkPasskeyMessageEditor();
    }
    if (current === 6 && !this.hasPrefillStep()) {
      this.leavePrefillStep();
    }
    if (current > this.summaryStepNumber && ids.includes(this.summaryStepNumber)) {
      this.wizardStep = this.summaryStepNumber;
      return;
    }
    const next = ids.find((id) => id > current);
    const prev = [...ids].reverse().find((id) => id < current);
    this.wizardStep = Number(next ?? prev ?? 1);
  }

  syncWizardSelectOptions() {
    this.tableOptionsTargets = (this.targets || []).map((t) => ({
      id: t.referenceId,
      name: t.referenceName || t.referenceId,
    }));
    if (this.isDefaultLinkEditWithUploadStep()) {
      const byId = new Map(this.tableOptionsTargets.map((t) => [String(t.id), t]));
      (this.availableReferences || []).forEach((ref) => {
        const id = String(ref.id);
        if (!byId.has(id)) {
          byId.set(id, { id: ref.id, name: ref.name || ref.id });
        }
      });
      this.tableOptionsTargets = [...byId.values()];
    }
    const redirectRef = this.linkRedirect?.referenceId
      ? this.referenceForTableOptions(this.linkRedirect.referenceId)
      : null;
    this.redirectFieldOptions = redirectRef ? this.tb.getRedirFields(redirectRef, this.PROVIDER) : [];
    const successRef = this.linkSuccessMessage?.referenceId
      ? this.referenceForTableOptions(this.linkSuccessMessage.referenceId)
      : null;
    this.successMessageFieldOptions = successRef ? this.tb.getSuccessMsgFields(successRef, this.PROVIDER) : [];
    const uploadRef = this.linkUpload?.referenceId
      ? this.referenceForTableOptions(this.linkUpload.referenceId)
      : null;
    this.uploadFieldOptions = uploadRef ? this.tb.getRefUploadFields(this.ts.template, uploadRef, this.PROVIDER) : [];
    this.filenameFieldOptions = uploadRef ? this.tb.getRefFilenameFields(uploadRef, this.PROVIDER) : [];
    this.syncImportDocFieldOptions();
    this.syncPasskeyReferenceOptions();
    this.syncWizardNav();
    if (this.isSummaryStep()) this.syncLinkSummary();
  }

  isLastStep() {
    return this.isSummaryStep();
  }

  syncLinkSummary() {
    this.linkSummary = this.summaryBuilder.fromWizardState({
      newField: this.newField,
      tables: this.tables,
      targets: this.targets,
      prefills: this.prefills,
      references: this.availableReferences,
      editLink: this.editLink,
      editLinkId: this.editLinkId,
      linkRedirect: this.linkRedirect,
      redirectOnLoad: this.redirectOnLoad,
      linkSuccessMessage: this.linkSuccessMessage,
      linkUpload: this.linkUpload,
      linkPasskeys: this.linkPasskeys,
      linkPasskeyMessage: this.linkPasskeyMessage,
      enableSaveAndKeepWorking: this.enableSaveAndKeepWorking,
      forcePdfDownload: this.forcePdfDownload,
      linkType: this.linkType,
      parseDocField: this.parseDocField,
    });
    if (this.isUrlLink()) {
      const linkId = this.editLinkId || this.linkSummary?.linkId;
      if (linkId) {
        this.linkSummary.shareUrl = this.resolveUrlLinkShareUrl(linkId);
      }
    }
  }

  stepHostValid() {
    const label = this.newField?.label;
    if (!label || !String(label).trim()) return false;
    if (!this.autoGenSuppliedConnectionValid()) return false;
    if (this.isUrlLink()) return true;
    const tableId = this.newField?.tableId;
    if (!tableId) return false;
    return !!(this.sourceSchema?.fields?.length);
  }

  stepMappingValid() {
    const targets = this.buildTargetsFromRows();
    if (this.isUrlLink()) {
      if (!targets.length) return true;
      return targets.every((t) => t.action === 'submit');
    }
    if (this.isAutomaticFileGenerationLink()) {
      if (!this.hasAutoGenPrintTarget()) return false;
      return !!(
        this.newField.tableId &&
        this.sourceSchema?.fields?.length &&
        targets.every((t) => this.targetHasRequiredParentField(t))
      );
    }
    if (!targets.length) {
      return !!(this.newField.tableId && this.sourceSchema?.fields?.length);
    }
    return !!(
      this.newField.tableId &&
      targets.every((t) => this.targetHasRequiredParentField(t))
    );
  }

  getMappingStepBlockedReason() {
    const targets = this.buildTargetsFromRows();
    if (!targets.length) {
      if (this.isUrlLink()) return '';
      if (this.isAutomaticFileGenerationLink()) {
        return 'Select at least one Print / view record target with a host key field.';
      }
      if (this.needsHostTable() && !this.newField.tableId) {
        return 'Select a launch table on the Basic Info step.';
      }
      return '';
    }
    if (this.isUrlLink() && targets.some((t) => t.action !== 'submit')) {
      return 'URL links only support Create new record targets.';
    }
    if (this.isAutomaticFileGenerationLink()) {
      if (targets.some((t) => t.action !== 'print')) {
        return 'Automatic file generation links only support Print / view record or Ignore / skip.';
      }
      if (!this.hasAutoGenPrintTarget()) {
        return 'Select at least one Print / view record target with a host key field.';
      }
    }
    const missingParent = targets.find((t) => !this.targetHasRequiredParentField(t));
    if (missingParent) {
      return `Select a host key field for ${missingParent.referenceName || 'each edit and print target'}.`;
    }
    if (this.needsHostTable() && !this.newField.tableId) {
      return 'Select a launch table on the Basic Info step.';
    }
    return '';
  }

  getAutoGenUploadBlockedReason() {
    if (!this.isAutomaticFileGenerationLink() || this.autoGenUploadValid()) return '';
    if (!this.newField?.tableId) return 'Select a launch table on the Basic Info step.';
    if (!this.linkUpload?.referenceId) return 'Select a target table for file upload.';
    if (this.linkUpload?.uploadField?.id == null) {
      return 'Select an attachment field on the target table.';
    }
    return 'Complete the file upload options before continuing.';
  }

  canAdvanceFromStep(step) {
    const id = Number(step);
    if (id === 1) {
      if (this.isAutomaticRecordImportLink()) {
        return !this.loadingHostSchema && this.importBasicInfoValid();
      }
      return !this.loadingHostSchema && this.stepHostValid();
    }
    if (id === 2) return this.stepMappingValid();
    if (id === 3 && this.isAutomaticFileGenerationLink()) return this.autoGenUploadValid();
    if (id === 4 && this.hasPasskeyStep()) {
      if (this.hasIncompleteDraftPasskey()) return false;
      return true;
    }
    if (id === 6 && this.hasPrefillStep() && this.hasIncompleteDraftPrefill()) return false;
    if (id === 3 || id === 5 || id === 6) return true;
    return false;
  }

  showFinishButton() {
    const id = this.currentWizardStepId();
    if (id < 2 || this.isSummaryStep()) return false;
    if (this.isAutomaticRecordImportLink()) {
      return id === 1 && this.importBasicInfoValid();
    }
    if (this.isAutomaticFileGenerationLink()) {
      return id === 3 && this.autoGenUploadValid();
    }
    return true;
  }

  canFinishFromStep(step) {
    const id = Number(step);
    if (this.isAutomaticRecordImportLink()) {
      return id === 1 && this.importBasicInfoValid();
    }
    if (id < 2 || id >= this.summaryStepNumber) return false;
    if (!this.stepMappingValid()) return false;
    if (this.isAutomaticFileGenerationLink() && !this.autoGenUploadValid()) return false;
    if (id === 4 && this.hasPasskeyStep() && this.hasIncompleteDraftPasskey()) return false;
    if (id === 6 && this.hasPrefillStep() && this.hasIncompleteDraftPrefill()) return false;
    return true;
  }

  getFinishBlockedReason() {
    if (this.isAutomaticRecordImportLink()) {
      return this.getImportParseFieldBlockedReason() || 'Complete the Basic Info step first.';
    }
    if (!this.stepMappingValid()) {
      return this.getMappingStepBlockedReason() || 'Complete table mapping first.';
    }
    const uploadReason = this.getAutoGenUploadBlockedReason();
    if (uploadReason) return uploadReason;
    if (this.isWizardStep(4) && this.hasPasskeyStep() && this.hasIncompleteDraftPasskey()) {
      return this.getDraftPasskeyBlockedReason();
    }
    if (this.isWizardStep(6) && this.hasPrefillStep() && this.hasIncompleteDraftPrefill()) {
      return this.getDraftPrefillBlockedReason();
    }
    return '';
  }

  commitCurrentStepBeforeLeave() {
    const skipSync = { skipSync: true };
    if (this.isWizardStep(2) && !this.isAutomaticRecordImportLink()) {
      this.syncTargetsFromRows();
      this.pruneLinkUploadForTargets(skipSync);
      this.pruneLinkPasskeysForTargets(skipSync);
      this.pruneLinkActionFieldsForTargets(skipSync);
    }
    if (this.isUploadWizardStep()) {
      this.pruneLinkUploadForTargets(skipSync);
      this.reconcileLinkUploadFields();
    }
    if (this.isWizardStep(4)) {
      this.syncLinkPasskeyMessageFromEditor();
      this.pruneLinkPasskeysForTargets(skipSync);
    }
    if (this.isActionsWizardStep()) {
      this.pruneLinkActionFieldsForTargets(skipSync);
      this.reconcileLinkActionFields();
    }
    if (this.isWizardStep(6) && this.hasPrefillStep()) {
      this.showForm = false;
      this.prefillDraftIncomplete = false;
      this.prunePrefillsForTargets();
    }
  }

  reconcileLinkUploadFields() {
    const refId = this.linkUpload?.referenceId;
    if (!refId) return;
    const ref = this.referenceForTableOptions(refId);
    if (!ref) return;
    const uploadFields = this.tb.getRefUploadFields(this.ts.template, ref, this.PROVIDER);
    const filenameFields = this.tb.getRefFilenameFields(ref, this.PROVIDER);
    if (this.linkUpload.uploadField?.id != null) {
      this.linkUpload.uploadField =
        uploadFields.find((f) => String(f.id) === String(this.linkUpload.uploadField.id)) || null;
    }
    if (this.linkUpload.filenameField?.id != null) {
      this.linkUpload.filenameField =
        filenameFields.find((f) => String(f.id) === String(this.linkUpload.filenameField.id)) || null;
    }
  }

  reconcileLinkActionFields() {
    if (this.linkRedirect.referenceId && this.linkRedirect.field?.id != null) {
      const ref = this.referenceForTableOptions(this.linkRedirect.referenceId);
      const fields = ref ? this.tb.getRedirFields(ref, this.PROVIDER) : [];
      this.linkRedirect.field =
        fields.find((f) => String(f.id) === String(this.linkRedirect.field.id)) || null;
      if (!this.linkRedirect.field) this.redirectOnLoad = false;
    }
    if (this.linkSuccessMessage.referenceId && this.linkSuccessMessage.field?.id != null) {
      const ref = this.referenceForTableOptions(this.linkSuccessMessage.referenceId);
      const fields = ref ? this.tb.getSuccessMsgFields(ref, this.PROVIDER) : [];
      this.linkSuccessMessage.field =
        fields.find((f) => String(f.id) === String(this.linkSuccessMessage.field.id)) || null;
    }
  }

  finishToReview($event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.canFinishFromStep(this.wizardStep)) {
      this.onFinishBlockedClick($event);
      return;
    }
    this.commitCurrentStepBeforeLeave();
    if (this.hasIncompleteDraftPasskey()) {
      this.clearDraftPasskey();
    }
    this.destroyLinkPasskeyMessageEditor();
    this.setWizardStep(this.summaryStepNumber);
    this.syncWizardNav();
    this.syncWizardSelectOptions();
  }

  onFinishBlockedClick($event) {
    let section = null;
    if (this.isWizardStep(2) && !this.stepMappingValid()) section = 'target';
    if (this.isWizardStep(4) && this.hasPasskeyStep() && this.hasIncompleteDraftPasskey()) {
      section = 'passkey';
    }
    if (this.isWizardStep(6) && this.hasPrefillStep() && this.hasIncompleteDraftPrefill()) {
      section = 'prefill';
    }
    if (!section) return;
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    this.highlightDraftSection(section);
  }

  isDraftPasskeyEmpty() {
    const { referenceId, passkeyField, placeholder } = this.draftLinkPasskey || {};
    return !referenceId && !passkeyField?.id && !String(placeholder || '').trim();
  }

  hasIncompleteDraftPasskey() {
    return !this.isDraftPasskeyEmpty();
  }

  hasIncompleteDraftPrefill() {
    return !!this.prefillDraftIncomplete;
  }

  getNextStepBlockedReason() {
    const step = this.wizardStep;
    if (step === 1 && this.loadingHostSchema) return 'Loading table fields…';
    if (step === 1 && this.isAutomaticRecordImportLink()) {
      return this.getImportParseFieldBlockedReason();
    }
    if (step === 2) {
      const mappingReason = this.getMappingStepBlockedReason();
      if (mappingReason) return mappingReason;
    }
    const uploadReason = this.getAutoGenUploadBlockedReason();
    if (step === 3 && uploadReason) return uploadReason;
    if (step === 4 && this.hasPasskeyStep() && this.hasIncompleteDraftPasskey()) {
      return this.getDraftPasskeyBlockedReason();
    }
    if (step === 6 && this.hasPrefillStep() && this.hasIncompleteDraftPrefill()) {
      return this.getDraftPrefillBlockedReason();
    }
    return '';
  }

  getDraftPrefillBlockedReason() {
    return 'Complete the prefill and click Add (+), or cancel before continuing.';
  }

  getDraftPasskeyBlockedReason() {
    const { referenceId, passkeyField, placeholder } = this.draftLinkPasskey || {};
    if (referenceId && !passkeyField?.id) {
      return 'Select a passkey field, then click Add (+), or clear the passkey form.';
    }
    if (referenceId && this.canAddDraftPasskey()) {
      return 'Click Add (+) to save this passkey, or clear the form before continuing.';
    }
    if (String(placeholder || '').trim() && !referenceId) {
      return 'Select a target table and passkey field, or clear the placeholder text.';
    }
    return 'Complete or clear the passkey form before continuing.';
  }

  isDraftSectionHighlighted(section) {
    return this.draftHighlightSection === section;
  }

  highlightDraftSection(section) {
    const selectors = {
      target: '.signed-link-target-mapping-list',
      passkey: '.signed-link-draft-passkey-panel',
      prefill: 'prefill .prefill-draft-panel',
    };
    const selector = selectors[section];
    if (!selector) return;
    this.draftHighlightSection = null;
    this.$timeout(() => {
      this.draftHighlightSection = section;
      const el = this.$window.document.querySelector(`signed-template-link ${selector}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      this.$timeout(() => {
        this.draftHighlightSection = null;
      }, 1800);
    });
  }

  onNextBlockedClick($event) {
    let section = null;
    if (this.isWizardStep(2) && !this.stepMappingValid()) section = 'target';
    if (this.isWizardStep(4) && this.hasPasskeyStep() && this.hasIncompleteDraftPasskey()) {
      section = 'passkey';
    }
    if (this.isWizardStep(6) && this.hasPrefillStep() && this.hasIncompleteDraftPrefill()) {
      section = 'prefill';
    }
    if (!section) return;
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    this.highlightDraftSection(section);
  }

  async handleNextClick($event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (this.canAdvanceFromStep(this.wizardStep)) {
      await this.nextStep();
      return;
    }
    this.onNextBlockedClick($event);
  }

  goToStep(step, $event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    const targetStep = Number(step);
    if (targetStep >= this.currentWizardStepId()) return;
    if (!this.getApplicableWizardStepIds().includes(targetStep)) return;
    if (this.isWizardStep(6) && this.hasPrefillStep() && targetStep < 6) this.leavePrefillStep();
    if (this.hasOptionalLinkSteps() && Number(this.wizardStep) >= 5 && targetStep < 5) {
      this.leaveTableOptionsStep();
    }
    if (this.isWizardStep(4) && targetStep < 4) this.leavePasskeyStep();
    if (this.hasOptionalLinkSteps() && Number(this.wizardStep) >= 3 && targetStep < 3) {
      this.leaveUploadStep();
    }
    this.setWizardStep(targetStep);
    this.syncWizardNav();
    if (this.isWizardStep(2) && !this.isAutomaticRecordImportLink()) this.enterMappingStep();
    this.syncWizardSelectOptions();
    this.syncPasskeyMessageEditorForStep();
  }

  syncPasskeyMessageEditorForStep() {
    if (this.isWizardStep(4) && this.hasPasskeyStep()) this.initLinkPasskeyMessageEditor();
    else this.destroyLinkPasskeyMessageEditor();
  }

  getPasskeyMessageEditorId() {
    return 'editor-signed-link-passkey';
  }

  getEditorConfig() {
    return {
      image_previewText: ' ',
      toolbarGroups: [
        { name: 'document', groups: ['mode', 'document', 'doctools'] },
        { name: 'clipboard', groups: ['clipboard', 'undo'] },
        { name: 'editing', groups: ['find', 'selection', 'spellchecker'] },
        { name: 'forms' },
        '/',
        { name: 'basicstyles', groups: ['basicstyles', 'cleanup'] },
        { name: 'paragraph', groups: ['list', 'indent', 'blocks', 'align', 'bidi'] },
        { name: 'links' }, { name: 'insert' },
        '/',
        { name: 'styles' }, { name: 'colors' }, { name: 'tools' }, { name: 'others' }, { name: 'about' },
      ],
      removeButtons: 'About,Anchor,BidiLtr,BidiRtl,Blockquote,Button,Checkbox,CopyFormatting,CreateDiv,CreatePlaceholder,ExportPdf,Flash,Form,HiddenField,ImageButton,Language,NewPage,PageBreak,Paste,PasteFromWord,PasteText,Print,Radio,RemoveFormat,Replace,Select,ShowBlocks,Smiley,SpecialChar,Strike,Subscript,Superscript,Textarea,TextField',
      removePlugins: 'exportpdf',
    };
  }

  initLinkPasskeyMessageEditor() {
    if (typeof CKEDITOR === 'undefined') return;
    CKEDITOR.disableAutoInline = true;
    this.$timeout(() => {
      const editorId = this.getPasskeyMessageEditorId();
      const el = document.getElementById(editorId);
      if (!el || CKEDITOR.instances[editorId]) return;
      const config = this.getEditorConfig();
      CKEDITOR.inline(editorId, { ...CKEDITOR.config, ...config });
    }, 100);
  }

  destroyLinkPasskeyMessageEditor() {
    const editorId = this.getPasskeyMessageEditorId();
    if (typeof CKEDITOR !== 'undefined' && CKEDITOR.instances[editorId]) {
      CKEDITOR.instances[editorId].destroy();
    }
  }

  syncLinkPasskeyMessageFromEditor() {
    const editorId = this.getPasskeyMessageEditorId();
    const editor = typeof CKEDITOR !== 'undefined' ? CKEDITOR.instances[editorId] : null;
    if (editor) this.linkPasskeyMessage = editor.getData();
  }

  toggleLinkPasskeyMessageHtmlView() {
    this.syncLinkPasskeyMessageFromEditor();
    this.showLinkPasskeyMessageHtml = !this.showLinkPasskeyMessageHtml;
    if (!this.showLinkPasskeyMessageHtml) this.initLinkPasskeyMessageEditor();
  }

  clearLinkPasskeyMessage() {
    this.linkPasskeyMessage = '';
    const editorId = this.getPasskeyMessageEditorId();
    const editor = typeof CKEDITOR !== 'undefined' ? CKEDITOR.instances[editorId] : null;
    if (editor) editor.setData('');
  }

  async prevStep() {
    if (this.currentWizardStepId() <= 1) return;
    if (this.isWizardStep(6) && this.hasPrefillStep()) this.leavePrefillStep();
    if (this.isActionsWizardStep()) this.leaveTableOptionsStep();
    if (this.isWizardStep(4)) this.leavePasskeyStep();
    if (this.isUploadWizardStep()) this.leaveUploadStep();
    this.setWizardStep(this.getPrevWizardStepId(this.wizardStep));
    this.syncWizardNav();
    this.syncWizardSelectOptions();
    this.syncPasskeyMessageEditorForStep();
  }

  isBackDisabled() {
    return false;
  }

  backButtonLabel() {
    return this.currentWizardStepId() <= 1 ? 'Links' : 'Back';
  }

  getFormulasMenuCtrl() {
    let scope = this.$scope.$parent;
    while (scope) {
      if (scope.formulasMenuCtrl) return scope.formulasMenuCtrl;
      if (scope.$ctrl && typeof scope.$ctrl.returnToLinksList === 'function') {
        return scope.$ctrl;
      }
      scope = scope.$parent;
    }
    return null;
  }

  handleBackClick() {
    if (this.currentWizardStepId() <= 1) {
      this.goBackToLinks();
      return;
    }
    this.prevStep();
  }

  goBackToLinks() {
    this.destroyLinkPasskeyMessageEditor();
    const menuCtrl = this.getFormulasMenuCtrl();
    if (menuCtrl) {
      menuCtrl.returnToLinksList();
    }
  }

  async nextStep() {
    if (!this.canAdvanceFromStep(this.wizardStep)) return;
    let advancedAfterAsync = false;
    if (this.isWizardStep(1)) {
      if (this.needsHostTable()) {
        const tableId = this.newField.tableId;
        if (tableId && !this.sourceSchema?.fields?.length) {
          await this.tableSelected(tableId);
          advancedAfterAsync = true;
          if (!this.stepHostValid()) return;
        }
      }
    }
    this.commitCurrentStepBeforeLeave();
    if (this.currentWizardStepId() < this.summaryStepNumber) {
      this.setWizardStep(this.getNextWizardStepId(this.wizardStep));
    }
    this.syncWizardNav();
    if (this.isWizardStep(2) && !this.isAutomaticRecordImportLink()) {
      this.enterMappingStep();
    }
    this.syncWizardSelectOptions();
    this.syncPasskeyMessageEditorForStep();
    if (advancedAfterAsync) {
      this.$timeout(() => {});
    }
  }

  leavePasskeyStep() {
    this.syncLinkPasskeyMessageFromEditor();
    this.destroyLinkPasskeyMessageEditor();
    this.pruneLinkPasskeysForTargets();
  }

  leavePrefillStep() {
    this.showForm = false;
    this.prefillDraftIncomplete = false;
    this.prunePrefillsForTargets();
  }

  leaveTableOptionsStep() {
    this.pruneLinkActionFieldsForTargets();
  }

  leaveUploadStep() {
    this.pruneLinkUploadForTargets();
  }

  async loadTargetTableSchema(referenceId) {
    if (!referenceId) return null;
    if (this._targetTableSchemas[referenceId]) return this._targetTableSchemas[referenceId];
    const ref = this.availableReferences.find((r) => r.id === referenceId);
    if (!ref?.tableId) return null;
    try {
      const schema = await this.ss.getSchema(this.ts.template, ref.tableId);
      this._targetTableSchemas[referenceId] = schema;
      return schema;
    } catch (error) {
      this.handleMessage({ error, when: 'Loading target table schema' });
      return null;
    }
  }

  referenceForTableOptions(referenceId) {
    const schema = referenceId ? this._targetTableSchemas[referenceId] : null;
    const ref = this.availableReferences.find((r) => r.id === referenceId);
    if (!ref) return null;
    return { ...ref, tableSchema: schema || ref.tableSchema };
  }

  async onLinkRedirectReferenceChange() {
    if (!this.linkRedirect.referenceId) {
      this.linkRedirect = { referenceId: null, field: null };
      this.redirectOnLoad = false;
      this.syncWizardSelectOptions();
      return;
    }
    this.linkRedirect.field = null;
    this.redirectOnLoad = false;
    await this.loadTargetTableSchema(this.linkRedirect.referenceId);
    this.syncWizardSelectOptions();
  }

  onLinkRedirectFieldChange() {
    if (!this.linkRedirect.field) this.redirectOnLoad = false;
  }

  async onLinkSuccessReferenceChange() {
    if (!this.linkSuccessMessage.referenceId) {
      this.linkSuccessMessage = { referenceId: null, field: null };
      this.syncWizardSelectOptions();
      return;
    }
    this.linkSuccessMessage.field = null;
    await this.loadTargetTableSchema(this.linkSuccessMessage.referenceId);
    this.syncWizardSelectOptions();
  }

  async onLinkUploadReferenceChange() {
    if (!this.linkUpload.referenceId) {
      this.resetLinkUpload();
      this.syncWizardSelectOptions();
      return;
    }
    this.linkUpload.uploadField = null;
    this.linkUpload.filenameField = null;
    this.linkUpload.uploadType = '';
    this.linkUpload.generateOnPrint = false;
    await this.loadTargetTableSchema(this.linkUpload.referenceId);
    this.syncWizardSelectOptions();
  }

  onLinkUploadFieldChange() {
    if (!this.linkUpload.uploadField) {
      this.linkUpload.uploadType = '';
      this.linkUpload.generateOnPrint = false;
    }
  }

  resetLinkUpload() {
    this.linkUpload = {
      referenceId: null,
      uploadField: null,
      filenameField: null,
      uploadType: '',
      generateOnPrint: false,
    };
  }

  pruneLinkUploadForTargets({ skipSync = false } = {}) {
    if (this.isDefaultLinkEditWithUploadStep() && this.isDownloadOnlyLink()) {
      if (!skipSync) this.syncWizardSelectOptions();
      return;
    }
    const targetRefIds = new Set((this.targets || []).map((t) => t.referenceId));
    if (this.linkUpload.referenceId && !targetRefIds.has(this.linkUpload.referenceId)) {
      this.resetLinkUpload();
    }
    if (!skipSync) this.syncWizardSelectOptions();
  }

  pruneDownloadOnOpenForTargets() {
    if (!this.canOfferDownloadOnOpen()) {
      this.forcePdfDownload = false;
    }
  }

  pruneLinkActionFieldsForTargets({ skipSync = false } = {}) {
    const targetRefIds = new Set((this.targets || []).map((t) => t.referenceId));
    if (this.linkRedirect.referenceId && !targetRefIds.has(this.linkRedirect.referenceId)) {
      this.linkRedirect = { referenceId: null, field: null };
      this.redirectOnLoad = false;
    }
    if (this.linkSuccessMessage.referenceId && !targetRefIds.has(this.linkSuccessMessage.referenceId)) {
      this.linkSuccessMessage = { referenceId: null, field: null };
    }
    if (!skipSync) this.syncWizardSelectOptions();
  }

  async onDraftPasskeyReferenceChange() {
    this.draftLinkPasskey.passkeyField = null;
    this.draftPasskeyFieldOptions = [];
    if (!this.draftLinkPasskey.referenceId) return;
    await this.loadTargetTableSchema(this.draftLinkPasskey.referenceId);
    const ref = this.referenceForTableOptions(this.draftLinkPasskey.referenceId);
    this.draftPasskeyFieldOptions = ref ? this.tb.getPasskeyFields(ref, this.PROVIDER) : [];
  }

  canAddDraftPasskey() {
    const eligible = new Set(this._passkeyEligibleReferenceIds || []);
    return !!(
      this.draftLinkPasskey.referenceId &&
      eligible.has(this.draftLinkPasskey.referenceId) &&
      this.draftLinkPasskey.passkeyField?.id != null
    );
  }

  addLinkPasskey($event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.canAddDraftPasskey()) return;
    const ref = (this.targets || []).find((t) => t.referenceId === this.draftLinkPasskey.referenceId);
    this.linkPasskeys.push({
      referenceId: this.draftLinkPasskey.referenceId,
      referenceName: ref?.referenceName || this.draftLinkPasskey.referenceId,
      passkeyField: this.draftLinkPasskey.passkeyField,
      placeholder: String(this.draftLinkPasskey.placeholder || '').trim(),
    });
    this.draftLinkPasskey = { referenceId: null, passkeyField: null, placeholder: '' };
    this.draftPasskeyFieldOptions = [];
    this.syncWizardSelectOptions();
  }

  removeLinkPasskey(index) {
    this.linkPasskeys.splice(index, 1);
    this.syncWizardSelectOptions();
  }

  pruneLinkPasskeysForTargets({ skipSync = false } = {}) {
    const eligible = new Set(this._passkeyEligibleReferenceIds || []);
    this.linkPasskeys = (this.linkPasskeys || []).filter((p) => eligible.has(p.referenceId));
    if (this.draftLinkPasskey.referenceId && !eligible.has(this.draftLinkPasskey.referenceId)) {
      this.draftLinkPasskey = { referenceId: null, passkeyField: null, placeholder: '' };
      this.draftPasskeyFieldOptions = [];
    }
    if (!this.hasPasskeyEligibleTargets()) {
      this.linkPasskeys = [];
      this.linkPasskeyMessage = '';
      this.showLinkPasskeyMessageHtml = false;
      this.clearLinkPasskeyMessage();
      this.draftLinkPasskey = { referenceId: null, passkeyField: null, placeholder: '' };
      this.draftPasskeyFieldOptions = [];
    }
    if (!skipSync) this.syncWizardSelectOptions();
  }

  resetLinkPasskeys() {
    this.linkPasskeys = [];
    this.linkPasskeyMessage = '';
    this.showLinkPasskeyMessageHtml = false;
    this.clearLinkPasskeyMessage();
    this.draftLinkPasskey = { referenceId: null, passkeyField: null, placeholder: '' };
    this.draftPasskeyFieldOptions = [];
  }

  buildLinkPasskeyOption() {
    this.syncLinkPasskeyMessageFromEditor();
    const entries = (this.linkPasskeys || [])
      .filter((p) => p.referenceId && p.passkeyField?.id != null)
      .map((p) => ({
        referenceId: p.referenceId,
        passkeyFieldId: String(p.passkeyField.id),
        ...(String(p.placeholder || '').trim()
          ? { passkeyPlaceholder: String(p.placeholder).trim() }
          : {}),
      }));
    if (!entries.length) return undefined;
    const DOMPurify = require('dompurify');
    const msgRaw = String(this.linkPasskeyMessage || '').trim();
    const passkeyMessage = msgRaw
      ? DOMPurify.sanitize(msgRaw, { USE_PROFILES: { html: true } })
      : '';
    return {
      entries,
      ...(passkeyMessage ? { passkeyMessage } : {}),
    };
  }

  buildLinkRedirectOption() {
    const refId = this.linkRedirect?.referenceId;
    const fieldId = this.linkRedirect?.field?.id;
    if (!refId || fieldId == null) return undefined;
    return {
      referenceId: refId,
      fieldId: String(fieldId),
      onLoad: this.isUrlLink() ? false : !!this.redirectOnLoad,
    };
  }

  buildLinkSuccessMessageOption() {
    const refId = this.linkSuccessMessage?.referenceId;
    const fieldId = this.linkSuccessMessage?.field?.id;
    if (!refId || fieldId == null) return undefined;
    return {
      referenceId: refId,
      fieldId: String(fieldId),
    };
  }

  buildLinkUploadOption() {
    const refId = this.linkUpload?.referenceId;
    const uploadFieldId = this.linkUpload?.uploadField?.id;
    const filenameFieldId = this.linkUpload?.filenameField?.id;
    if (!refId || (uploadFieldId == null && filenameFieldId == null)) return undefined;
    const option = { referenceId: refId };
    if (uploadFieldId != null) option.uploadFieldId = String(uploadFieldId);
    if (filenameFieldId != null) option.filenameFieldId = String(filenameFieldId);
    if (uploadFieldId != null) {
      option.uploadType = this.linkUpload.uploadType ?? '';
      if (!this.isAutomaticFileGenerationLink()) {
        option.genActions = {
          submit: true,
          update: true,
          print: !!this.linkUpload.generateOnPrint,
        };
      }
    }
    return option;
  }

  updateEligibleReferenceIds() {
    this._eligibleReferenceIds = (this.targets || [])
      .filter((t) => t.action === 'submit' || t.action === 'update')
      .map((t) => t.referenceId);
    this._passkeyEligibleReferenceIds = (this.targets || [])
      .filter((t) => t.action === 'update' || t.action === 'print')
      .map((t) => t.referenceId);
  }

  hasPrefillEligibleTargets() {
    return (this._eligibleReferenceIds || []).length > 0;
  }

  hasPasskeyEligibleTargets() {
    return (this._passkeyEligibleReferenceIds || []).length > 0;
  }

  resetSourceTable() {
    this.prefills = [];
    this.sourceSchema = {};
    this.sourceKeyFields = [];
    this._targetTableSchemas = {};
    this.linkRedirect = { referenceId: null, field: null };
    this.redirectOnLoad = false;
    this.linkSuccessMessage = { referenceId: null, field: null };
    this.resetLinkUpload();
    this.resetLinkPasskeys();
    this.parseDocField = null;
  }

  async linkTypeSelected(linkType) {
    if (this.isEditModeActive()) return;
    const prevType = this._lastLinkType ?? this.linkType;
    if (prevType === linkType) return;

    if (linkType === 'url') {
      const hasUrlModeLoss =
        (this.targetRows || []).some(
          (row) => row.action !== 'ignore' && row.action !== 'submit'
        ) ||
        (this.prefills || []).length > 0 ||
        (this.linkPasskeys || []).length > 0;
      if (hasUrlModeLoss) {
        const msg =
          'Switching to a URL link will remove passkeys, pre-fills, and non-submit targets. Continue?';
        if (!this.$window.confirm(msg)) {
          this.$timeout(() => {
            this.linkType = prevType;
          });
          return;
        }
      }
      this.linkType = 'url';
      this.applyUrlLinkMode();
      this.syncDefaultLinkLabelForType('url', prevType);
      this._lastLinkType = 'url';
      this.syncWizardNav();
      this.syncWizardSelectOptions();
      return;
    }

    if (prevType === 'url') {
      this.newField.tableId = '';
      this.sourceSchema = { fields: [] };
      this.sourceKeyFields = [];
      this._lastHostTableId = '';
    }

    this.linkType = linkType;
    if (linkType === 'generate') {
      this.enableSaveAndKeepWorking = false;
      this.applyAutomaticFileGenerationMode();
      this.initAutoGenSuppliedConnection();
    } else if (linkType === 'import') {
      if (this.isDynamicTemplate()) {
        this.$timeout(() => {
          this.linkType = prevType;
        });
        this.handleMessage({
          error: new Error('Automatic Record Import is not supported for dynamic templates.'),
          when: 'Selecting link type',
        });
        return;
      }
      this.enableSaveAndKeepWorking = false;
      this.applyAutomaticRecordImportMode();
      this.initAutoGenSuppliedConnection();
    } else {
      this.suppliedConnection = null;
    }
    this.syncDefaultLinkLabelForType(linkType, prevType);
    this._lastLinkType = linkType;
    this.syncWizardNav();
    this.syncWizardSelectOptions();
  }

  async tableSelected(tableId) {
    if (this.isEditModeActive()) return;
    if (!this.needsHostTable()) return;
    if (!tableId) {
      this.loadingHostSchema = false;
      return;
    }
    if (this.loadingHostSchema) {
      if (this._hostSchemaLoadPromise) await this._hostSchemaLoadPromise;
      return;
    }
    try {
      const msg = 'Changing the launch table will reset target parent fields and prefills. Continue?';
      if (this._lastHostTableId && this.wouldLoseHostTableConfiguration() && !this.$window.confirm(msg)) {
        this.$timeout(() => {
          this.newField.tableId = this._lastHostTableId || '';
        });
        return;
      }
      this.loadingHostSchema = true;
      this.newField.tableId = tableId;
      this.resetSourceTable();
      this.targets = [];
      this.initTargetRows();
      this._hostSchemaLoadPromise = this.ss.getSchema(this.ts.template, tableId);
      this.sourceSchema = await this._hostSchemaLoadPromise;
      this.sourceKeyFields = this.getKeyFields(this.sourceSchema.fields);
      this._lastHostTableId = tableId;
      this.updateEligibleReferenceIds();
    } catch (error) {
      this.handleMessage({ error, when: 'Loading launch table schema' });
    } finally {
      this.loadingHostSchema = false;
      this._hostSchemaLoadPromise = null;
    }
    this.syncWizardSelectOptions();
    this.$timeout(() => {});
  }

  normalizeRecordNotFoundAction(value) {
    if (value === 'ignore' || value === 'warn' || value === 'create') return value;
    return 'error';
  }

  normalizeOnEmptyRecordAction(value) {
    return value === 'ignore' ? 'ignore' : 'create';
  }

  hasParentFieldSelected(parentField) {
    return parentField != null && parentField.id != null && String(parentField.id).length > 0;
  }

  targetHasRequiredParentField(target) {
    if (target.action !== 'update' && target.action !== 'print') return true;
    return this.hasParentFieldSelected(target.parentField);
  }

  clearDraftPasskey($event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.hasIncompleteDraftPasskey()) return;
    this.draftLinkPasskey = { referenceId: null, passkeyField: null, placeholder: '' };
    this.draftPasskeyFieldOptions = [];
  }

  prunePrefillsForTargets() {
    this.updateEligibleReferenceIds();
    const eligible = new Set(this._eligibleReferenceIds || []);
    const targetRefIds = new Set((this.targets || []).map((t) => t.referenceId));
    const sourceFieldIds = new Set(
      (this.sourceSchema?.fields || []).map((f) => String(f.id))
    );
    const seen = new Set();
    this.prefills = (this.prefills || []).filter((p) => {
      const refId = p.reference?.id;
      if (!refId || !eligible.has(refId) || !targetRefIds.has(refId)) return false;
      const srcId = p.sourceField?.id;
      const tgtId = p.targetField?.id;
      if (srcId == null || tgtId == null) return false;
      if (!sourceFieldIds.has(String(srcId))) return false;
      const dedupeKey = `${refId}:${tgtId}`;
      if (seen.has(dedupeKey)) return false;
      seen.add(dedupeKey);
      return true;
    });
  }

  buildFieldMap() {
    const fieldMap = {};
    (this.targets || []).forEach((t) => {
      if ((t.action === 'update' || t.action === 'print') && t.parentField) {
        fieldMap[t.referenceId] = { recordIdField: t.parentField };
      }
    });
    return this.applyPrefills(fieldMap);
  }

  applyPrefills(fieldMap = {}) {
    const acc = { ...fieldMap };
    const eligible = new Set(this._eligibleReferenceIds || []);
    const targetRefIds = new Set((this.targets || []).map((t) => t.referenceId));
    const byRef = {};
    for (const p of this.prefills || []) {
      const refId = p.reference?.id;
      if (!refId || !eligible.has(refId) || !targetRefIds.has(refId)) continue;
      if (p.targetField?.id == null || p.sourceField?.id == null) continue;
      if (!byRef[refId]) byRef[refId] = [];
      byRef[refId].push({ targetField: p.targetField, sourceField: p.sourceField });
    }
    for (const refId of Object.keys(byRef)) {
      acc[refId] = { ...acc[refId], prefills: byRef[refId] };
    }
    return acc;
  }

  formValid() {
    this.syncTargetsFromRows();
    if (!this.newField.label) return false;
    if (!this.autoGenSuppliedConnectionValid()) return false;
    if (this.isAutomaticRecordImportLink()) {
      return this.importBasicInfoValid();
    }
    if (!this.stepMappingValid()) return false;
    if (this.isAutomaticFileGenerationLink() && !this.autoGenUploadValid()) return false;
    return true;
  }

  buildCreatedShareSummary(shareUrl, label, linkId) {
    const summary = this.summaryBuilder.fromWizardState({
      newField: { ...this.newField, label: label || this.newField.label || '' },
      tables: this.tables,
      targets: this.targets,
      prefills: this.prefills,
      references: this.availableReferences,
      editLink: this.editLink,
      editLinkId: linkId || this.editLinkId,
      linkRedirect: this.linkRedirect,
      redirectOnLoad: this.redirectOnLoad,
      linkSuccessMessage: this.linkSuccessMessage,
      linkUpload: this.linkUpload,
      linkPasskeys: this.linkPasskeys,
      linkPasskeyMessage: this.linkPasskeyMessage,
      enableSaveAndKeepWorking: this.enableSaveAndKeepWorking,
      forcePdfDownload: this.forcePdfDownload,
      linkType: this.linkType,
      parseDocField: this.parseDocField,
    });
    summary.type = this.linkType;
    summary.shareUrl = shareUrl || '';
    return summary;
  }

  applyUrlLinkSuccess(label, shareUrl, linkId) {
    const safeLabel = label || this.newField.label || '';
    const safeUrl = shareUrl || '';
    this.createdFieldName = safeLabel;
    this.createdFieldUrl = safeUrl;
    this.createdShareSummary = this.buildCreatedShareSummary(safeUrl, safeLabel, linkId);
    this.$timeout(() => {});
  }

  resolveUrlLinkShareUrl(linkId) {
    const template = this.ts.template;
    if (!template?.id || !linkId) return '';
    return this.urlClipCopy.getUrlLinkUrl(template, linkId);
  }

  submitCreate($event) {
    if ($event) $event.preventDefault();
    this.save();
  }

  save() {
    if (this.isEditMode) return this.updateLink();
    return this.add();
  }

  buildSignedLinkPayload() {
    this.syncTargetsFromRows();
    const downloadOnly = this.isDownloadOnlyLink();
    const fieldMap = this.isUrlLink() || this.isAutomaticRecordImportLink()
      || (downloadOnly && !this.isAutomaticFileGenerationLink())
      ? {}
      : this.buildFieldMap();
    const action = downloadOnly
      ? 'print'
      : (this.isAutomaticFileGenerationLink()
        ? (this.targets[0]?.action || 'print')
        : (this.isAutomaticRecordImportLink()
          ? 'print'
          : this.targets[0].action));
    return {
      ...(this.isUrlLink() ? {} : { tableId: this.newField.tableId }),
      label: this.newField.label,
      signed: true,
      type: this.linkType,
      action,
      targets: this.isAutomaticRecordImportLink()
        ? []
        : this.targets.map((t) => ({
          referenceId: t.referenceId,
          action: t.action,
          ...(t.parentField ? { parentField: t.parentField } : {}),
          ...((t.action === 'update' || t.action === 'print') && this.isTableLink()
            ? { recordNotFoundAction: this.normalizeRecordNotFoundAction(t.recordNotFoundAction) }
            : {}),
          ...(this.shouldIncludeOnEmptyRecordAction(t)
            ? { onEmptyRecordAction: this.normalizeOnEmptyRecordAction(t.onEmptyRecordAction) }
            : {}),
        })),
      fieldMap,
      forcePdfDownload: this.forcePdfDownload,
      enableSaveAndKeepWorking: this.resolveEnableSaveAndKeepWorkingForPayload(downloadOnly),
      ...(this.isTableLink() || this.isUrlLink()
        ? { redirect: this.buildLinkRedirectOption() }
        : {}),
      ...(this.isTableLink() ? { successMessage: this.buildLinkSuccessMessageOption() } : {}),
      upload: this.buildLinkUploadOption(),
      ...(this.isTableLink() ? { passkey: this.buildLinkPasskeyOption() } : {}),
      ...(this.isAutomaticRecordImportLink() && this.parseDocField?.id != null
        ? { parseField: { fieldId: String(this.parseDocField.id) } }
        : {}),
      ...(this.buildAutoGenSuppliedConnectionPayload()),
    };
  }

  buildAutoGenSuppliedConnectionPayload() {
    if (!this.requiresAutoGenSuppliedConnection()) return {};
    const userToken = this.suppliedConnection?.userToken;
    if (typeof userToken !== 'string' || !userToken.trim()) return {};
    return {
      suppliedConnection: { userToken: userToken.trim() },
    };
  }

  async updateLink() {
    if (!this.formValid() || this.saving || !this.editLinkId) return;
    this.prunePrefillsForTargets();
    this.pruneLinkUploadForTargets();
    this.pruneLinkPasskeysForTargets();
    this.pruneLinkActionFieldsForTargets();

    try {
      this.saving = true;
      this.messages = [];
      const payload = {
        linkId: this.editLinkId,
        ...this.buildSignedLinkPayload(),
        label: this.editLink?.field?.label || this.newField.label,
      };
      const request = this.as.includeConnectionInRequest({
        payload: {
          action: 'updateLinks',
          templateId: this.ts.template.id,
          links: [payload],
        },
      });
      const response = await this.ts.linkManagement(request);
      const data = response?.data || {};
      if (!data.updatedLinks?.length) {
        throw new Error('The link could not be updated.');
      }
      const label = this.editLink?.field?.label || this.newField.label;
      if (this.isUrlLink()) {
        const shareUrl = this.resolveUrlLinkShareUrl(this.editLinkId);
        this.applyUrlLinkSuccess(label, shareUrl);
      } else {
        this.createdShareSummary = null;
        this.createdFieldName = label;
        this.createdFieldUrl = this.editLink?.field?.publicUrl || '';
      }
    } catch (error) {
      this.handleMessage({ error, when: 'when updating the smart link' });
    }
    this.saving = false;
  }

  async add() {
    if (!this.formValid() || this.saving) return;
    this.prunePrefillsForTargets();
    this.pruneLinkUploadForTargets();
    this.pruneLinkPasskeysForTargets();
    this.pruneLinkActionFieldsForTargets();

    if (this.isUrlLink()) {
      try {
        this.saving = true;
        this.messages = [];
        const request = this.as.includeConnectionInRequest({
          payload: {
            action: 'createLink',
            templateId: this.ts.template.id,
            link: this.buildSignedLinkPayload(),
          },
        });
        const response = await this.ts.linkManagement(request);
        const created = response?.data?.createdLink;
        if (!created?.linkId) {
          throw new Error('The URL link could not be created.');
        }
        this.applyUrlLinkSuccess(
          created.label || this.newField.label,
          this.resolveUrlLinkShareUrl(created.linkId),
          created.linkId
        );
      } catch (error) {
        this.handleMessage({ error, when: 'when creating the URL link' });
      }
      this.saving = false;
      return;
    }

    const msg =
      'Adding a link will prevent adding and removing target tables on this template. Continue?';
    const willPublish = !this.ts.template.published;
    if (willPublish && !this.$window.confirm(msg)) return;

    try {
      this.saving = true;
      this.messages = [];
      const res = await this.tb.addTemplateFieldLink(this.buildSignedLinkPayload());
      const { messages, newLink } = res.data || {};
      const label = newLink?.label;
      const publicUrl = newLink?.publicUrl;
      const linkId = newLink?.linkId;
      if (willPublish) {
        this.ts.notifyUpdate(['published'], { template: { published: true } }, true);
      }
      messages.forEach((msg) => this.handleMessage(msg));
      this.createdFieldName = label;
      this.createdFieldUrl = publicUrl;
      if (this.isAutomaticFileGenerationLink() && linkId) {
        this.editLinkId = linkId;
      }
      if (this.isAutomaticRecordImportLink() && linkId) {
        this.editLinkId = linkId;
      }
    } catch (error) {
      this.handleMessage({ error, when: 'when creating the smart link' });
    }
    this.saving = false;
  }

  handleMessage(rawMessage) {
    this.messages = this.fe.updateMessages(this.messages, rawMessage);
  }

  cancel() {
    this.destroyLinkPasskeyMessageEditor();
    this.onCancel();
  }

  $onDestroy() {
    this.destroyLinkPasskeyMessageEditor();
  }
}

export default {
  bindings: {
    editLink: '<',
    onCancel: '&',
    initialWizardStepId: '<?',
    preselectUploadReferenceId: '<?',
  },
  templateUrl:
    'form-builder/build-wizard/signed-template-link/signed-template-link.component.html',
  controller: SignedTemplateLinkCtrl,
};
